import TopNav from "@/components/TopNav";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { HelpCircle, ArrowRight } from "lucide-react";

const FAQS = [
  {
    q: "What is an Open Topic Credit?",
    a: "One Open Topic Credit unlocks TWO COMPLETE AI-generated learning topics (0.5 credit each) — that's the analogy, the story, the key concepts, AND the illustration bundled together. The same 0.5-credit-per-topic cost applies on free, Plus, and Pro. You never spend credits per image regen or per chat message inside a topic you've already unlocked. Free accounts get 6 one-time credits at signup (12 topics). Plus refreshes 10 credits every month (20 topics). Pro refreshes 20 credits every month (40 topics).",
  },
  {
    q: "How does the 10 (Plus) / 20 (Pro) monthly credit limit work?",
    a: "Every paid plan credits your account on your billing anniversary — Plus gets 10, Pro gets 20 fresh Open Topic Credits. Each complete AI-generated topic costs 0.5 credit, so Plus effectively unlocks 20 topics/month and Pro unlocks 40 topics/month. Unused credits do NOT roll over between billing periods (this keeps pricing simple and predictable). If you burn through your monthly credits early, top-up packs are available at any time and those top-up credits stack on your account without expiring while your subscription is active.",
  },
  {
    q: "Do the flashcards and analogies recycle so I don't waste credits?",
    a: "Yes — every analogy, story, and illustration you generate is saved to a shared library. If you (or anyone else on PictureMed) already generated a card for a topic like 'Michaelis-Menten kinetics' or 'Plasma Membrane,' the next student who opens that topic sees the cached version instantly at no credit cost. The 'Generate more flashcards' button in each category is also cached — you'll see a 'Loaded from library' banner and can hit 'Regenerate fresh set' if you want a brand-new pass.",
  },
  {
    q: "How does PictureMed actually help pre-med students?",
    a: "Pre-meds forget most of what they cram because their brains store rote facts as forgettable text. PictureMed teaches every hard-science concept — Bio, Biochem, Gen Chem, O-Chem, Physics, Psych/Soc — through a vivid analogy paired with a cartoon image and a short story. Your brain remembers stories and pictures for months longer than definitions. On top of the visuals you get: a full question bank across every AAMC content category, a CARS passage analyzer, a spaced-repetition homework queue that surfaces your weakest topics first, and full-length practice tests modeled on the MCAT's timing and section structure.",
  },
  {
    q: "What is the AI Study Coach and who can use it?",
    a: "The AI Study Coach is a High-Yield MCAT expert powered by Google Gemini 2.5 Flash. It's exclusive to the Pro plan. It always leads with an everyday-life analogy (kitchens, traffic, sports) and then ties it back to the mechanism, so you actually understand — not just memorize. It can explain any answer you got wrong, walk through problems step-by-step, generate custom quizzes, and build day-by-day study plans for your exam date. Pro subscribers get 20 tutor questions per day.",
  },
  {
    q: "Do I need Pro to take a full-length practice test?",
    a: "Free accounts can take one half-length practice test (115 questions) so you can feel the format. Full-length tests (230 questions across all 4 MCAT sections) and unlimited half-lengths are Plus and Pro only. The test engine mirrors real MCAT timing, section structure, break screens, and passage-based format.",
  },
  {
    q: "Can I cancel my subscription anytime?",
    a: "Yes. Cancel any time from the Plans page — you'll keep every paid feature until the end of your current billing period, and then your account rolls back to Free. Your saved decks, analogies, streak, and progress data all stay with your account regardless of plan.",
  },
  {
    q: "Is PictureMed affiliated with the AAMC?",
    a: "No. PictureMed is an independent study tool and is not affiliated with, endorsed by, or sponsored by the Association of American Medical Colleges (AAMC). MCAT® is a registered trademark of the AAMC. All practice content is modeled on the exam's publicly documented timing, section structure, and passage-based format.",
  },
];

export default function FAQ() {
  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <TopNav />
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="faq-page">
        <div className="pm-badge mb-4 inline-flex" style={{ background: "var(--hl-blue)", color: "var(--primary)" }}>
          <HelpCircle size={12} className="mr-1.5" /> FAQ
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold mb-3">
          Everything you were about to ask.
        </h1>
        <p className="text-lg mb-8" style={{ color: "var(--text-secondary)" }}>
          Straight answers on credits, recycling, the AI Tutor, and how PictureMed
          actually helps you remember the MCAT.
        </p>

        <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="pm-card px-5"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="text-left font-display font-semibold text-base md:text-lg">
                {f.q}
              </AccordionTrigger>
              <AccordionContent
                className="text-sm md:text-base leading-relaxed"
                style={{ color: "var(--text-secondary)" }}
              >
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div
          className="pm-card p-6 mt-10 flex items-center justify-between gap-4 flex-wrap"
          style={{ background: "var(--hl-yellow)" }}
          data-testid="faq-cta-card"
        >
          <div>
            <div className="font-display font-bold text-lg">Still have questions?</div>
            <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
              The fastest way to know if PictureMed clicks for you is to try it — free plan takes 30 seconds.
            </div>
          </div>
          <Link
            to="/auth"
            className="pm-btn-primary inline-flex items-center gap-1.5 text-sm"
            data-testid="faq-cta-btn"
          >
            Get Started <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
